import React, { useState, useEffect } from 'react';
import { projectAPI, userAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Users, Loader2, FolderOpen, Check, Crown } from 'lucide-react';
import { toast } from 'sonner';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
};

export default function ProjectTeam() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [memberIds, setMemberIds] = useState([]);
  const [pmId, setPmId] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, uRes] = await Promise.all([projectAPI.getProjects(), userAPI.getUsers()]);
        setProjects(pRes.data || []);
        setAllUsers((uRes.data || []).filter(u => u.role !== 'super_admin'));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const selectProject = async (proj) => {
    setSelectedProject(proj);
    const existing = (proj.members || []).map(m => m.id);
    setMemberIds(existing);
    setPmId(proj.pm?.id || null);
  };

  const toggleMember = (uid) => {
    setMemberIds(prev => {
      const next = prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid];
      // If removing the current PM, clear PM selection
      if (!next.includes(pmId)) setPmId(null);
      return next;
    });
  };

  const saveMembers = async () => {
    if (!selectedProject) return;
    setSaving(true);
    try {
      await projectAPI.updateMembers(selectedProject.id, memberIds, pmId);
      toast.success(`Team updated for ${selectedProject.name}`);
      const res = await projectAPI.getProjects();
      setProjects(res.data || []);
      const updated = (res.data || []).find(p => p.id === selectedProject.id);
      if (updated) {
        setSelectedProject(updated);
        setPmId(updated.pm?.id || null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update team');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="animate-spin text-[#002FA7]" size={28} />
    </div>
  );

  const selectedMembers = allUsers.filter(u => memberIds.includes(u.id));

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto" data-testid="project-team-page">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-lg sm:text-xl font-bold text-slate-800">Project Teams</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-1">Assign team members to projects.</p>
      </div>

      <div className="flex flex-col lg:grid lg:grid-cols-12 gap-4 sm:gap-6">
        {/* Project List */}
        <div className="lg:col-span-4 space-y-2" data-testid="project-list">
          <div className="flex lg:hidden items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Projects</span>
            <span className="text-[10px] text-slate-400">({projects.length})</span>
          </div>
          <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 scrollbar-hide">
          {projects.map(p => {
            const memberCount = (p.members || []).length;
            const isSelected = selectedProject?.id === p.id;
            return (
              <button key={p.id} onClick={() => selectProject(p)}
                className={`text-left p-3 rounded-lg border transition-all flex-shrink-0 lg:flex-shrink lg:w-full min-w-[180px] lg:min-w-0 ${isSelected ? 'border-[#002FA7] bg-[#002FA7]/5 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                data-testid={`project-select-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0 ${isSelected ? 'bg-[#002FA7] text-white' : 'bg-slate-100 text-slate-500'}`}>
                      {p.client?.code || p.name?.substring(0, 3).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                      <p className="text-[10px] text-slate-400">PM: {p.pm?.name || 'Unassigned'}</p>
                    </div>
                  </div>
                  <span className="flex items-center gap-1 text-[10px] text-slate-400 flex-shrink-0 ml-2">
                    <Users size={10} /> {memberCount}
                  </span>
                </div>
              </button>
            );
          })}
          </div>
        </div>

        {/* Members Panel */}
        <div className="lg:col-span-8" data-testid="members-panel">
          {!selectedProject ? (
            <div className="text-center py-20 bg-white rounded-lg border border-dashed border-slate-200">
              <FolderOpen className="mx-auto text-slate-300 mb-3" size={36} />
              <p className="text-sm text-slate-400">Select a project to manage its team</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-slate-200">
              <div className="p-4 border-b border-slate-100">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="text-sm font-bold text-slate-800">{selectedProject.name}</h2>
                    <p className="text-xs text-slate-400 mt-0.5">{memberIds.length} member{memberIds.length !== 1 ? 's' : ''} selected</p>
                  </div>
                  <button onClick={saveMembers} disabled={saving}
                    className="h-8 px-4 text-xs font-semibold rounded-md text-white bg-[#002FA7] hover:bg-[#002585] disabled:opacity-50 flex items-center gap-1.5"
                    data-testid="save-members-btn">
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                    Save
                  </button>
                </div>
                {/* PM Selector — only PM-role users */}
                {allUsers.some(u => u.role === 'pm') && (
                  <div className="flex items-center gap-2">
                    <Crown size={14} className="text-amber-500" />
                    <span className="text-xs font-medium text-slate-500">Project Manager:</span>
                    <select
                      value={pmId || ''}
                      onChange={(e) => setPmId(e.target.value || null)}
                      className="text-xs border border-slate-200 rounded-md px-2 py-1 outline-none focus:border-[#002FA7] bg-white text-slate-700"
                      data-testid="pm-select"
                    >
                      <option value="">Select PM</option>
                      {allUsers.filter(u => u.role === 'pm').map(u => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="p-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {allUsers.map(u => {
                    const isMember = memberIds.includes(u.id);
                    const isPM = u.id === pmId;
                    return (
                      <button key={u.id} onClick={() => toggleMember(u.id)}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border text-left transition-all ${isMember ? 'border-[#002FA7] bg-[#002FA7]/5' : 'border-slate-100 hover:border-slate-200'}`}
                        data-testid={`member-toggle-${u.id}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isMember ? 'bg-[#002FA7] text-white' : 'bg-slate-200 text-slate-500'}`}>
                          {getInitials(u.name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-medium text-slate-700 truncate">{u.name}</p>
                            {isPM && <Crown size={12} className="text-amber-500 flex-shrink-0" />}
                          </div>
                          <p className="text-[10px] text-slate-400 truncate">{u.email} · {u.role}</p>
                        </div>
                        {isMember && <Check size={14} className="text-[#002FA7] flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
